<script lang="ts">
  import { popup, type PopupSettings } from '@skeletonlabs/skeleton'
  import { onDestroy } from 'svelte'

  import { type ThemePreference, themeStore } from '@stores/theme-store'

  import Icon from '$lib/components/Icon.svelte'

  const preferences: { value: ThemePreference; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ]

  const resolvedLabels = {
    light: 'Light',
    dark: 'Dark',
  }

  let preference = $state<ThemePreference>('system')
  let mode = $state<'light' | 'dark'>('dark')

  const unsubscribe = themeStore.subscribe((state) => {
    preference = state.preference
    mode = state.mode
  })

  onDestroy(() => {
    unsubscribe()
  })

  let ariaLabel = $state('')

  $effect(() => {
    const currentLabel = preferences.find((p) => p.value === preference)?.label ?? 'System'
    const systemSuffix =
      preference === 'system' ? ` (${resolvedLabels[mode]} - System setting)` : ''
    ariaLabel = `Theme: ${currentLabel}${systemSuffix}`
  })

  const popupSettings: PopupSettings = {
    event: 'click',
    target: 'theme-dropdown',
    placement: 'bottom-end',
  }

  const selectPreference = (value: ThemePreference) => {
    themeStore.setPreference(value)
  }
</script>

<button
  type="button"
  class="inline-flex items-center gap-1.5 rounded-full border border-surface-400 bg-surface-50 px-3.5 py-1.5 text-sm font-semibold text-[rgb(var(--on-surface))] transition-colors duration-150 hover:bg-surface-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:border-surface-600 dark:bg-surface-700 dark:hover:bg-surface-600"
  aria-label={ariaLabel}
  title={ariaLabel}
  use:popup={popupSettings}
>
  <span class="flex items-center justify-center" aria-hidden="true">
    {#if preference === 'system'}
      <Icon icon="iconoir:system-restart" class="h-5 w-5" />
    {:else if preference === 'dark'}
      <Icon icon="iconoir:half-moon" class="h-5 w-5" />
    {:else}
      <Icon icon="iconoir:sun-light" class="h-5 w-5" />
    {/if}
  </span>

  <span class="whitespace-nowrap">
    {preferences.find((p) => p.value === preference)?.label}
  </span>

  <span class="ml-0.5 flex items-center" aria-hidden="true">
    <svg viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4">
      <path
        fill-rule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clip-rule="evenodd"
      />
    </svg>
  </span>
</button>

<div
  class="z-[1000] min-w-[140px] rounded-lg bg-surface-100 p-1 shadow-lg dark:bg-surface-800"
  data-popup="theme-dropdown"
>
  <ul class="m-0 list-none p-0">
    {#each preferences as pref (pref.value)}
      <li>
        <button
          type="button"
          class={`flex w-full items-center gap-2 rounded-md border-0 bg-transparent px-3 py-2 text-sm font-medium text-[rgb(var(--on-surface))] transition-colors duration-150 hover:bg-surface-200 dark:hover:bg-surface-700 ${
            preference === pref.value ? 'bg-surface-200 dark:bg-surface-700' : ''
          }`}
          onclick={() => selectPreference(pref.value)}
        >
          <span class="flex items-center justify-center" aria-hidden="true">
            {#if pref.value === 'system'}
              <Icon icon="iconoir:system-restart" class="h-[1.1rem] w-[1.1rem]" />
            {:else if pref.value === 'dark'}
              <Icon icon="iconoir:half-moon" class="h-[1.1rem] w-[1.1rem]" />
            {:else}
              <Icon icon="iconoir:sun-light" class="h-[1.1rem] w-[1.1rem]" />
            {/if}
          </span>

          <span class="flex-1 text-left">{pref.label}</span>

          {#if preference === pref.value}
            <span class="flex items-center text-primary-500" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4">
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
          {/if}
        </button>
      </li>
    {/each}
  </ul>
</div>
